/**
 * Persists CIG box note write-back for the parent order and linked siblings.
 * @module saveCigBoxNoteChangesForOrderIds
 */
import { SiblingOrdersResponse } from "../data-model/siblingOrdersModel";
import { resolveLinkedOrderUpdateOrderIds } from "./resolveLinkedOrderUpdateOrderIds";
import {
  saveCigBoxNoteChanges,
  SaveCigBoxNoteParams,
  SaveCigBoxNoteResult,
} from "./saveCigBoxNoteChanges";

/** Parameters for {@link saveCigBoxNoteChanges} excluding the target order id. */
export type SaveCigBoxNoteBaseParams = Omit<SaveCigBoxNoteParams, "orderId">;

/** Optional per-order field overrides for linked sibling orders. */
export type SaveCigBoxNoteOrderOverrides = Partial<
  Pick<SaveCigBoxNoteParams, "dateOfService" | "drugName">
>;

/**
 * Builds a resolver that applies filtered-sibling write-back fields per order id.
 *
 * The parent order keeps the base payload unchanged. Only linked sibling ids
 * receive overrides sourced from {@link filteredSiblingOrders}.
 *
 * @param parentOrderId - Current order id being written back from the BO page
 * @param filteredSiblingOrders - Filtered sibling orders from the data accumulator
 * @param defaultDrugName - Fallback regimen name when a sibling has none
 * @returns Resolver for {@link saveCigBoxNoteChangesForOrderIds}
 */
export const buildSiblingOrderOverridesResolver = (
  parentOrderId: string,
  filteredSiblingOrders: SiblingOrdersResponse | null | undefined,
  defaultDrugName = "",
): ((orderId: string) => SaveCigBoxNoteOrderOverrides) => {
  return (orderId: string): SaveCigBoxNoteOrderOverrides => {
    if (orderId === parentOrderId) {
      return {};
    }

    const sibling = filteredSiblingOrders?.siblings?.find(
      (linkedOrder) => linkedOrder.order_id === orderId,
    );

    if (!sibling) {
      return {};
    }

    return {
      dateOfService: sibling.order?.payload?.date_of_service ?? "",
      drugName: sibling.order?.payload?.regimen_name ?? defaultDrugName,
    };
  };
};

/**
 * Saves the same CIG box note payload for each target order id in parallel.
 *
 * @param baseParams - Shared write-back fields for every order
 * @param parentOrderId - Current order being written back from the BO page
 * @param selectedOrderIds - Optional linked sibling order ids from the modal
 * @param getOrderOverrides - Optional resolver for sibling-specific fields
 * @returns One result per target order, in the same order as resolved ids
 */
export const saveCigBoxNoteChangesForOrderIds = async (
  baseParams: SaveCigBoxNoteBaseParams,
  parentOrderId: string,
  selectedOrderIds?: string[],
  getOrderOverrides?: (orderId: string) => SaveCigBoxNoteOrderOverrides,
): Promise<SaveCigBoxNoteResult[]> => {
  const orderIdsToUpdate = resolveLinkedOrderUpdateOrderIds(
    parentOrderId,
    selectedOrderIds,
  );

  return Promise.all(
    orderIdsToUpdate.map((orderId) =>
      saveCigBoxNoteChanges({
        ...baseParams,
        ...getOrderOverrides?.(orderId),
        orderId,
      }),
    ),
  );
};
